/**
 * Utility functions for converting between dial values, Tone.js notation, and display format
 * for BPM-relative delay times.
 */

// Available note values: half note to 32nd note
export const NOTE_VALUES = ["2n", "4n", "8n", "16n", "32n"] as const;
export type NoteValue = (typeof NOTE_VALUES)[number];

// Display format mapping
const NOTE_DISPLAY: Record<NoteValue, string> = {
  "2n": "1/2",
  "4n": "1/4",
  "8n": "1/8",
  "16n": "1/16",
  "32n": "1/32",
};

/**
 * Converts a dial value (0-1) to a Tone.js note notation
 * Maps: 0.0-0.2 → "2n", 0.2-0.4 → "4n", 0.4-0.6 → "8n", 0.6-0.8 → "16n", 0.8-1.0 → "32n"
 */
export const dialValueToNoteValue = (dialValue: number): NoteValue => {
  const clamped = Math.max(0, Math.min(1, dialValue));
  const index = Math.floor(clamped * NOTE_VALUES.length);
  // Handle edge case where value is exactly 1.0
  return NOTE_VALUES[Math.min(index, NOTE_VALUES.length - 1)];
};

/**
 * Converts a Tone.js note notation to a dial value (0-1)
 */
export const noteValueToDialValue = (noteValue: NoteValue): number => {
  const index = NOTE_VALUES.indexOf(noteValue);
  if (index === -1) {
    // Default to quarter note if invalid
    return 0.3; // Middle of 0.2-0.4 range for "4n"
  }
  // Return middle of the range for each note value
  const rangeSize = 1 / NOTE_VALUES.length;
  return index * rangeSize + rangeSize / 2;
};

/**
 * Converts Tone.js notation to display format (e.g., "4n" → "1/4")
 */
export const noteValueToDisplay = (noteValue: NoteValue): string => {
  return NOTE_DISPLAY[noteValue] || "1/4";
};

/**
 * Converts display format to Tone.js notation (e.g., "1/4" → "4n")
 */
export const displayToNoteValue = (display: string): NoteValue => {
  const entry = Object.entries(NOTE_DISPLAY).find(
    ([_, value]) => value === display
  );
  return (entry?.[0] as NoteValue) || "4n";
};

/**
 * Validates if a string is a valid note value
 */
export const isValidNoteValue = (value: string): value is NoteValue => {
  return NOTE_VALUES.includes(value as NoteValue);
};

/**
 * Converts old numeric delay time (in seconds) to a default note value
 * Used for migrating old presets
 */
export const secondsToDefaultNoteValue = (_seconds: number): NoteValue => {
  // Default to quarter note for migration
  return "4n";
};
